import { useState, useEffect, useCallback, useRef } from 'react';
import { API_URL } from '@/lib/api';

interface TradingStatus {
  isOpen: boolean;
  nextOpenTime: string | null;
  nextCloseTime: string | null;
  countdown: string;
  countdownSeconds: number;
  openTime: string;
  closeTime: string;
  timezone: string;
  weekendEnabled: boolean;
  enabled: boolean;
  manualPaused: boolean;
  pauseReason: string | null;
  pausedCategories: string[];
}

function pad(n: number) {
  return String(n).padStart(2, '0');
}

/**
 * 带指数退避重试的 fetch
 */
async function fetchWithRetry(
  url: string,
  options: { method?: string; headers?: Record<string, string> } = {},
  retries = 2,
): Promise<Response> {
  let lastErr: unknown;
  for (let i = 0; i < retries; i += 1) {
    try {
      const res = await fetch(url, options);
      if (res.ok) return res;
      // 5xx 才重试，4xx 不重试
      if (res.status < 500) return res;
      lastErr = new Error(`HTTP ${res.status}`);
    } catch (e) {
      lastErr = e;
    }
    if (i < retries - 1) {
      await new Promise((r) => { setTimeout(r, 800 * 2 ** i); });
    }
  }
  throw lastErr;
}

/**
 * 交易时段状态
 * 数据源：自建后端 /api/trading-status（读 MySQL trading_schedule）
 * 不再依赖 Supabase 云函数；后端不可用时保持上一次状态，不阻塞用户交易
 */
export function useTradingStatus() {
  const [status, setStatus] = useState<TradingStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const fetchingRef = useRef(false);

  const fetchStatus = useCallback(async () => {
    // 防止并发重复请求
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    try {
      const res = await fetchWithRetry(`${API_URL}/trading-status`, {
        method: 'GET',
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = (await res.json()) as { data?: TradingStatus };
      if (json.data) {
        setStatus(json.data);
        setError(false);
      }
    } catch {
      // 后端不可用：保留上一次已知状态，避免误判休市导致无法下单
      setError(true);
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // 本地倒计时更新（每秒减1秒）
  useEffect(() => {
    if (!status) return undefined;

    const interval = setInterval(() => {
      setStatus((prev) => {
        if (!prev) return prev;
        const nextSeconds = Math.max(0, prev.countdownSeconds - 1);
        const hours = Math.floor(nextSeconds / 3600);
        const minutes = Math.floor((nextSeconds % 3600) / 60);
        const seconds = nextSeconds % 60;
        return {
          ...prev,
          countdownSeconds: nextSeconds,
          countdown: `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`,
        };
      });
    }, 1000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status?.countdownSeconds]);

  // 每30秒重新拉一次服务器状态（页面隐藏时暂停，省流量）
  useEffect(() => {
    const interval = setInterval(() => {
      if (typeof document !== 'undefined' && document.hidden) return;
      fetchStatus();
    }, 30000);
    return () => clearInterval(interval);
  }, [fetchStatus]);

  return {
    status, loading, error, refresh: fetchStatus,
  };
}